<template>
  <div class="section-container">
    <div class="section-header">
      <h1 class="section-title">考試成績管理</h1>
      <h2> {{ exam?.name || ''}} | {{ examType }} | {{ examDate }}</h2>
      <div class="table-controls">
        <div class="search-add-group">
          <input
            type="text"
            v-model="filterText"
            placeholder="搜索學生..."
            class="search-input"
          />
          <button class="button button-primary" @click="openModal(null)">新增成績</button>
        </div>
      </div>
    </div>

    <div class="table-container">
      <table class="data-table" v-if="sortedAndFilteredScores.length > 0">
        <thead>
          <tr>
            <th @click="sortBy('student_id')">
              學生ID
              <span v-if="sortColumn === 'student_id'" class="sort-icon">
                {{ sortDirection === 'asc' ? '↑' : '↓' }}
              </span>
            </th>
            <th @click="sortBy('name')">
              學生姓名
              <span v-if="sortColumn === 'name'" class="sort-icon">
                {{ sortDirection === 'asc' ? '↑' : '↓' }}
              </span>
            </th>
            <th @click="sortBy('score')">
              分數
              <span v-if="sortColumn === 'score'" class="sort-icon">
                {{ sortDirection === 'asc' ? '↑' : '↓' }}
              </span>
            </th>
            <th @click="sortBy('percentage')">
              百分比
              <span v-if="sortColumn === 'percentage'" class="sort-icon">
                {{ sortDirection === 'asc' ? '↑' : '↓' }}
              </span>
            </th>
            <th @click="sortBy('grade')">
              等第
              <span v-if="sortColumn === 'grade'" class="sort-icon">
                {{ sortDirection === 'asc' ? '↑' : '↓' }}
              </span>
            </th>
            <th @click="sortBy('rank')">
              排名
              <span v-if="sortColumn === 'rank'" class="sort-icon">
                {{ sortDirection === 'asc' ? '↑' : '↓' }}
              </span>
            </th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr 
            v-for="score in sortedAndFilteredScores" 
            :key="score.id"
            class="clickable-row"
            @click="handleRowClick(score.student.student_id, $event)"
          >
            <td>{{ score.student.student_id }}</td>
            <td>{{ score.student.name }}</td>
            <td>
              <span :class="getScoreClass(score.score, exam?.full_score)">
                {{ score.score || '-' }}
              </span>
            </td>
            <td>{{ score.percentage ? `${score.percentage.toFixed(1)}%` : '-' }}</td>
            <td>
              <span :class="`grade-badge ${score.grade?.toLowerCase() || 'none'}`">
                {{ score.grade || '-' }}
              </span>
            </td>
            <td>{{ score.rank || '-' }}</td>
            <td class="actions" @click.stop>
              <button class="button button-secondary" @click="openModal(score)">編輯</button>
              <button class="button button-danger" @click="confirmDelete(score.id)">刪除</button>
            </td>
          </tr>
        </tbody>
      </table>

      <div v-else class="empty-state">
        <p>尚無學生成績紀錄</p>
        <button class="button button-primary" @click="openModal(null)">新增第一筆成績</button>
      </div>
    </div>

    <ExamScoreModal
      v-if="showModal"
      :score="currentScore"
      :exam="exam"
      :isEdit="isEditMode"
      @close="closeModal"
      @saved="handleSaved"
    />
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useMutation } from '@vue/apollo-composable';
import { DELETE_STUDENT_EXAM_SCORE } from '../graphql/queries';
import ExamScoreModal from './ExamScoreModal.vue';

const router = useRouter();
const typeOptions = [
  { label: '期中考', value: 'MIDTERM' },
  { label: '期末考', value: 'FINAL' },
  { label: '週考', value: 'WEEKLY' },
  { label: '第一次段考', value: 'FIRST_TERM' },
  { label: '第二次段考', value: 'SECOND_TERM' },
  { label: '第三次段考', value: 'THIRD_TERM' },
  { label: '隨堂考', value: 'QUIZ' },
  { label: '模擬考', value: 'MOCK' },
  { label: '月考', value: 'MONTHLY' },
  { label: '單元測驗', value: 'UNIT_TEST' },
  { label: '複習考', value: 'REVIEW_TEST' },
  { label: '分班測驗', value: 'PLACEMENT' },
  { label: '證照考試', value: 'CERTIFICATION' },
  { label: '競賽', value: 'COMPETITION' },
];

const props = defineProps({
  exam: {
    type: Object,
    required: true
  }
});

const emit = defineEmits(['saved', 'deleteScore']);

// Modal state
const showModal = ref(false);
const currentScore = ref(null);
const isEditMode = ref(false);

// Sorting state
const sortColumn = ref('rank');
const sortDirection = ref('asc');

// Filter state
const filterText = ref('');

// GraphQL mutations
const { mutate: deleteScore } = useMutation(DELETE_STUDENT_EXAM_SCORE);

// Computed properties
const examScores = computed(() => props.exam?.student_scores || []);
const examDate = computed(() => {
  if (!props.exam || !props.exam?.exam_date) return '';
  const date = new Date(props.exam.exam_date);
  
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();
  
  return `${month}/${day}/${year}`;
});
const examType = computed(() => {
  const typeObj = typeOptions.find(t => t.value === props.exam?.type);
  return typeObj ? typeObj.label : '未知類型';
});

// Combined sorted and filtered scores
const sortedAndFilteredScores = computed(() => {
  let filtered = examScores.value;
  
  // Apply filtering if search text exists
  const searchText = filterText.value.trim().toLowerCase();
  if (searchText) {
    filtered = filtered.filter(score =>
      score.student.name.toLowerCase().includes(searchText) ||
      score.student.student_id.toString().includes(searchText) ||
      (score.grade && score.grade.toLowerCase().includes(searchText))
    );
  }
  
  // Apply sorting
  return [...filtered].sort((a, b) => {
    let valA, valB;
    
    switch (sortColumn.value) {
      case 'student_id':
        valA = parseInt(a.student.student_id);
        valB = parseInt(b.student.student_id);
        break;
      case 'name':
        valA = a.student.name;
        valB = b.student.name;
        break;
      case 'score':
        valA = a.score || 0;
        valB = b.score || 0;
        break;
      case 'percentage':
        valA = a.percentage || 0;
        valB = b.percentage || 0;
        break;
      case 'grade':
        valA = a.grade || '';
        valB = b.grade || '';
        break;
      case 'rank':
        valA = a.rank || 9999;
        valB = b.rank || 9999;
        break;
      default:
        valA = a[sortColumn.value];
        valB = b[sortColumn.value];
    }
    
    // Handle null values for sorting
    if (valA === null || valA === undefined) return 1;
    if (valB === null || valB === undefined) return -1;
    
    if (typeof valA === 'string') {
      valA = valA.toLowerCase();
      valB = valB.toLowerCase();
    }
    
    if (valA < valB) {
      return sortDirection.value === 'asc' ? -1 : 1;
    }
    if (valA > valB) {
      return sortDirection.value === 'asc' ? 1 : -1;
    }
    return 0;
  });
});

// Helper functions
function handleRowClick(studentId, event) {
  const isButtonClick = event.target.closest('button') !== null;
  if(!isButtonClick) {
    // Navigate to student detail page
    router.push(`/students/${studentId}`);
  }
}

function getScoreClass(score, fullScore) {
  if (score === null || score === undefined) return 'score-missing';
  
  const percentage = fullScore ? (score / fullScore) * 100 : 0;
  
  if (percentage >= 90) return 'score-excellent';
  if (percentage >= 80) return 'score-good';
  if (percentage >= 70) return 'score-average';
  if (percentage >= 60) return 'score-pass';
  return 'score-fail';
}

function sortBy(column) {
  if (sortColumn.value === column) {
    sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc';
  } else {
    sortColumn.value = column;
    sortDirection.value = 'asc';
  }
}

function openModal(score) {
  currentScore.value = score;
  isEditMode.value = !!score;
  showModal.value = true;
}

function closeModal() {
  showModal.value = false;
}

function handleSaved() {
  emit('saved');
  closeModal();
}

function confirmDelete(scoreId) {
  if (confirm('确定要删除这个成绩记录吗？此操作不可撤销。')) {
    deleteScore({ id: scoreId }).then(() => {
      emit('deleteScore', scoreId);
    }).catch((error) => {
      console.error('删除成绩失败:', error);
      alert('删除成绩失败: ' + error.message);
    });
  }
}
</script>

<style scoped>
.table-container {
  overflow-x: auto;
}

/* Score styling */
.score-excellent {
  color: #4CAF50;
  font-weight: bold;
}

.score-good {
  color: #2196F3;
  font-weight: bold;
}

.score-average {
  color: #FF9800;
  font-weight: bold;
}

.score-pass {
  color: #9C27B0;
}

.score-fail {
  color: #f44336;
  font-weight: bold;
}

.score-missing {
  color: var(--text-light);
  font-style: italic;
}

/* Grade badges */
.grade-badge {
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.8rem;
  color: white;
  font-weight: bold;
}

.grade-badge.a {
  background-color: #4CAF50;
}

.grade-badge.b {
  background-color: #2196F3;
}

.grade-badge.c {
  background-color: #FF9800;
}

.grade-badge.d {
  background-color: #9C27B0;
}

.grade-badge.f {
  background-color: #f44336;
}

.grade-badge.none {
  background-color: var(--text-light);
  color: var(--text-dark);
}

.actions {
  display: flex;
  gap: 8px;
  cursor: default;
}

.button-danger {
  background-color: var(--danger-color);
  color: white;
}

.button-danger:hover {
  background-color: var(--danger-hover);
}

/* Sorting styles */
th {
  cursor: pointer;
  position: relative;
  user-select: none;
}

th:hover {
  background-color: var(--light-bg);
}

.sort-icon {
  margin-left: 4px;
  font-size: 0.8em;
}

.table-controls {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.search-input {
  padding: 0.5rem 1rem;
  height: 32px;
  border: 1px solid var(--border-color);
  border-radius: 4px;
  font-size: 0.9rem;
  width: 220px;
  transition: all 0.2s ease;
  box-sizing: border-box;
}

.search-input:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 2px rgba(76, 175, 80, 0.2);
}

.search-add-group {
  display: flex;
  align-items: center;
  gap: 0;
}

.search-add-group .search-input {
  border-radius: 4px 0 0 4px;
  border-right: none;
}

.search-add-group .button {
  border-radius: 0 4px 4px 0;
}

.empty-state {
  padding: 3rem;
  text-align: center;
  color: var(--text-medium);
  background-color: var(--light-bg);
  border-radius: 4px;
  margin-top: 1rem;
}

.empty-state .button {
  margin-top: 1rem;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .table-controls {
    flex-direction: column;
    align-items: flex-end;
    gap: 0.75rem;
    width: 100%;
  }
  
  .search-add-group {
    width: 100%;
  }
  
  .search-add-group .search-input {
    flex-grow: 1;
    width: auto;
  }
}
</style>